import { themedUseStyletron } from '../../../theme/customTheme'
import { SHAPE } from 'baseui/button';
import { LabelLarge, LabelMedium, LabelXSmall, ParagraphSmall, ParagraphXSmall } from 'baseui/typography';
import { IBacnetDevice, RoutedSource, StartupState } from './types'
import { StyledLink } from 'baseui/link';
import { useGetMatchQuery, useGetRelationsOutByIdQuery } from '../../../services/objects';
import { Badge, COLOR } from 'baseui/badge';
import { Link } from 'react-router-dom';
import { getStateColor } from './utils';
import { FormattedDate, FormattedMessage, FormattedTime, useIntl } from 'react-intl';
import { ValueDisplayWithLabel } from '../../components/ValueDisplay';
import { Block } from 'baseui/block';
import { Banner } from 'baseui/banner';
import { HorizontalStackPanel } from '../../../components/Layout/StackPanel';
import { useSyncDeviceTimeMutation } from '../../../services/mapping/bacnet';
import { useReportMutationState } from '../../../hooks/useReportMutationState';
import { AccessRoles, useHasRole } from '../../../hooks/useHasRole';
import { modelId } from '../EdgeDevice';
import { Spinner } from 'baseui/icon';

export function InfoPanel(props: IBacnetDevice) {
    const [css, theme] = themedUseStyletron();
    const intl = useIntl();
    const { data: parents, isLoading, isSuccess, error } = useGetRelationsOutByIdQuery({ objectId: props.id, resourceGroupId: props.resourceGroupId, type: 'IsConnectedTo' });

    const parent = parents ? parents[0] : undefined;
    const stateColor = getStateColor(theme, props);

    const utcOffset = props.uTCOffset !== undefined ? props.uTCOffset * -1 / 60 + (props.isDaylightSaving ? 1 : 0) : undefined

    const routedSource: RoutedSource = props.routedSource ? JSON.parse(props.routedSource) : undefined;

    const isNotTimeSync = Math.abs(props.timeDiffInMintes) > 5;
    return (
        <>
            {!props.noConnection &&
                <HorizontalStackPanel>
                    <Badge hierarchy='secondary' color='positive' content={<FormattedMessage id="BACnetDevice.IsConnected" defaultMessage="Is connected" />} />
                    <LabelXSmall className={css({ color: stateColor })} margin={0}>{StartupState[props.startupState]}</LabelXSmall>
                </HorizontalStackPanel>
            }
            <ValueDisplayWithLabel label="DeviceId" value={props.deviceId} small dimension={props.ipAddress && `[${props.ipAddress}]`}></ValueDisplayWithLabel>
            {routedSource &&
                <ValueDisplayWithLabel label="Routed Source" value={``} dimension={`${routedSource.Type} Network ${routedSource.Network} Address [${routedSource.Address}]`} small></ValueDisplayWithLabel>
            }
            {props.vendorName && <LabelMedium>{props.vendorName}</LabelMedium>}
            {props.modelName && <ParagraphXSmall margin={0}>{props.modelName}</ParagraphXSmall>}
            {(props.protocolRevision > 0) && <ParagraphXSmall margin={0}>{props.protocolVersion}.{props.protocolRevision}</ParagraphXSmall>}
            {props.localDate &&
                <>
                    <Block display='inline-flex' alignItems={'baseline'}>
                        <ValueDisplayWithLabel small dimension='' label={<FormattedMessage id="BACnetDevice.localDate" defaultMessage='local Date' />} value={intl.formatDate(new Date(props.localDate), { day: '2-digit', month: 'long', year: 'numeric' })}></ValueDisplayWithLabel>
                        {utcOffset !== undefined && <ParagraphXSmall margin={0}>UTC {utcOffset > 0 ? `+${utcOffset}` : utcOffset}</ParagraphXSmall>}
                    </Block>
                    {isNotTimeSync && <WrongDeviceTime {...props} />}
                </>
            }
            <ParagraphXSmall margin={0}><Badge content={props.usedObjects} shape={SHAPE.pill} color={COLOR.accent}></Badge> <FormattedMessage id="BACnetDevice.bacnetObjects" defaultMessage='BACnet Objects' /></ParagraphXSmall>

            <Link to={`/edgedevices/${parent?.name}/${props.deviceId}/bacnetconnector?resourceGroupId=${props.resourceGroupId}`} className={css({ textDecoration: 'none' })}>
                <StyledLink><FormattedMessage id="BACnetDevice.showBACnetExplorer" defaultMessage='show Bacnet Explorer' /></StyledLink>
            </Link>
        </>
    );
}

const WrongDeviceTime = (props: IBacnetDevice) => {
    const intl = useIntl();
    const { data: edgeDevices, error, isLoading, isSuccess } = useGetMatchQuery({ objectId: props.id, resourceGroupId: props.resourceGroupId, modelId: modelId, relationship: 'ContainedIn' });
    const edgeDevice = edgeDevices?.[0];
    const [syncTime, syncTimeResult] = useSyncDeviceTimeMutation();
    useReportMutationState(syncTimeResult);

    const handleSyncTime = () => {
        syncTime({ deviceId: props.deviceId, edgeDeviceId: edgeDevice?.name });
    }

    const hasRole = useHasRole(AccessRoles.Mapping.Write);
    const enableSyncTime = hasRole && edgeDevice;

    const action = enableSyncTime ? {
        label: intl.formatMessage({ id: 'BacnetDevice.SyncTime', defaultMessage: 'Synchronize time'}),
        onClick: handleSyncTime
    } : undefined;

    if ( syncTimeResult.isLoading) 
        return (<><Spinner></Spinner>{' '}<ParagraphSmall><FormattedMessage id="BacnetDevice.IsTimeSyncing" defaultMessage={'Time is synchronized...'}></FormattedMessage></ParagraphSmall></>)

    return (<Banner hierarchy='low' kind='warning' title={<FormattedMessage id="BACnetDevice.wrongDeviceTime" defaultMessage="Time on Device is wrong" />} action={action}>
        <FormattedMessage id="BACnetDevice.deviceTimeDiff" defaultMessage="Difference is {timeDiff} min" values={
            { "timeDiff": <b>{props.timeDiffInMintes.toFixed(0)}</b> }
        } /></Banner>)
}